import { useI18n } from '../../../i18n';
// CategoryFormModal - Add / Edit

import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useCategories } from '../../../hooks';
import { ImageUploadField } from '../../../components/shared';

export interface CategoryFormData {
  name: string;
  parentId: string;
  sortOrder: string;
  isActive: boolean | null;
  /** Ảnh danh mục — BE bật 03/09/2026 (sheet 2 mục #14). */
  imageUrls: string[];
}

export type CategoryFormErrors = Partial<Record<keyof CategoryFormData, string>>;
type CategoryFormTouched = Partial<Record<keyof CategoryFormData, boolean>>;

export const emptyForm: CategoryFormData = {
  name: '',
  parentId: '',
  sortOrder: '0',
  isActive: null,
  imageUrls: [],
};

// `imageUrls` KHÔNG nằm trong danh sách này: nó không có validation và không
// thuộc thứ tự focus khi submit lỗi.
const formFieldOrder: Array<keyof CategoryFormData> = [
  'name', 'parentId', 'sortOrder', 'isActive',
];

export function slugify(value: string): string {
  return value.trim().toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Bug #209: thông báo validation phải đi qua i18n — nhận `t` thay vì chuỗi cứng. */
function validateField(
  field: keyof CategoryFormData,
  form: CategoryFormData,
  t: (key: string) => string
): string | undefined {
  if (field === 'name' && !form.name.trim()) return t('validationCategoryNameRequired');
  if (field === 'sortOrder') {
    const v = Number(form.sortOrder);
    if (!Number.isInteger(v) || v < 0) return t('validationSortOrderNonNegativeInteger');
  }
  return undefined;
}

function validateForm(form: CategoryFormData, t: (key: string) => string): CategoryFormErrors {
  const errors: CategoryFormErrors = {};
  for (const field of formFieldOrder) {
    const msg = validateField(field, form, t);
    if (msg) errors[field] = msg;
  }
  return errors;
}

export interface CategoryOption {
  id: string;
  label: string;
}

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Searchable combobox — fetches from server on search
function ParentCombobox({
  value,
  excludeId,
  initialLabel,
  onChange,
}: {
  value: string;
  excludeId?: string | null;
  initialLabel?: string;
  onChange: (id: string, label: string) => void;
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(initialLabel ?? '');
  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounce(search, 300);

  const { data, isFetching } = useCategories({
    page: 1,
    limit: 50,
    search: debouncedSearch || undefined,
  });

  const options: CategoryOption[] = useMemo(() => {
    const list = Array.isArray(data)
      ? data
      : Array.isArray((data as { results?: unknown[] })?.results)
        ? (data as { results: { id: string; name: string }[] }).results
        : [];
    return (list as { id: string; name: string }[])
      .filter((c) => c.id !== excludeId)
      .map((c) => ({ id: c.id, label: c.name }));
  }, [data, excludeId]);

  useEffect(() => {
    if (initialLabel) setSelectedLabel(initialLabel);
  }, [initialLabel]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div
        className="flex h-10 w-full cursor-pointer items-center rounded-lg border border-slate-200 px-3 text-sm focus-within:ring-1 focus-within:ring-blue-400"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={t('searchParent')}
            className="w-full outline-none bg-transparent text-slate-700"
          />
        ) : (
          <span className={`flex-1 truncate ${value ? 'text-slate-700' : 'text-slate-400'}`}>
            {value ? selectedLabel || value : t('selectParent')}
          </span>
        )}
        <svg className="ml-2 h-4 w-4 shrink-0 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          <div
            className="cursor-pointer px-3 py-2 text-sm text-slate-400 hover:bg-slate-50"
            onMouseDown={() => { onChange('', ''); setSelectedLabel(''); setOpen(false); setSearch(''); }}
          >
            — No parent
          </div>
          {isFetching ? (
            <div className="px-3 py-2 text-sm text-slate-400">{t('searching')}</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">{t('noResults')}</div>
          ) : (
            options.map((o) => (
              <div
                key={o.id}
                onMouseDown={() => { onChange(o.id, o.label); setSelectedLabel(o.label); setOpen(false); setSearch(''); }}
                className={`cursor-pointer px-3 py-2 text-sm hover:bg-blue-50 ${o.id === value ? 'bg-blue-50 font-medium text-blue-700' : 'text-slate-700'}`}
              >
                {o.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

interface CategoryFormModalProps {
  mode: 'create' | 'edit';
  initialData: CategoryFormData;
  categoryOptions?: CategoryOption[];
  activeCategoryId?: string | null;
  initialParentLabel?: string;
  isLoading?: boolean;
  isSubmitting?: boolean;
  onSubmit: (data: CategoryFormData) => Promise<void>;
  onClose: () => void;
}

export function CategoryFormModal({
  mode, initialData, activeCategoryId, initialParentLabel,
  isLoading, isSubmitting, onSubmit, onClose,
}: CategoryFormModalProps) {
  const { t } = useI18n();
  const [formData, setFormData] = useState<CategoryFormData>(initialData);
  const [formErrors, setFormErrors] = useState<CategoryFormErrors>({});
  const [formTouched, setFormTouched] = useState<CategoryFormTouched>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fieldRefs = useRef<Partial<Record<keyof CategoryFormData, HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null>>>({});

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleFieldChange = useCallback((field: keyof CategoryFormData, value: string | boolean | null) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value } as CategoryFormData;
      if (formTouched[field]) {
        setFormErrors((e) => ({ ...e, [field]: validateField(field, next, t) }));
      }
      return next;
    });
  }, [formTouched, t]);

  const handleBlur = useCallback((field: keyof CategoryFormData) => {
    setFormTouched((prev) => ({ ...prev, [field]: true }));
    setFormErrors((prev) => ({ ...prev, [field]: validateField(field, formData, t) }));
  }, [formData, t]);

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    const errors = validateForm(formData, t);
    setFormErrors(errors);
    setFormTouched(formFieldOrder.reduce<CategoryFormTouched>((acc, k) => ({ ...acc, [k]: true }), {}));
    const firstInvalid = formFieldOrder.find((f) => errors[f]);
    if (firstInvalid) { fieldRefs.current[firstInvalid]?.focus(); return; }
    try {
      await onSubmit(formData);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('failedToSaveCategory'));
    }
  }, [formData, onSubmit, t]);

  const statusValue = formData.isActive === true ? 'active' : formData.isActive === false ? 'inactive' : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h3 className="text-lg font-semibold text-slate-900">{mode === 'create' ? t('addCategory') : t('editCategory')}</h3>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:text-slate-600">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="border-t border-slate-100 px-6 py-5 space-y-4 overflow-y-auto max-h-[70vh]">
          {isLoading ? (
            <div className="py-8 text-center text-sm text-slate-500">{t('loading')}</div>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-sm text-slate-600">{t('categoryName')}</label>
                <input
                  ref={(el) => { fieldRefs.current.name = el; }}
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  onBlur={() => handleBlur('name')}
                  placeholder={t('enterCategoryName')}
                  className={`h-10 w-full rounded-lg border px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${formErrors.name && formTouched.name ? 'border-red-300' : 'border-slate-200'}`}
                />
                {formErrors.name && formTouched.name && <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>}
              </div>

              <div>
                <label className="mb-1 block text-sm text-slate-600">{t('parent')}</label>
                <ParentCombobox
                  value={formData.parentId}
                  excludeId={activeCategoryId}
                  initialLabel={initialParentLabel}
                  onChange={(id, label) => {
                    handleFieldChange('parentId', id);
                    void label;
                  }}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-slate-600">{t('sortOrder')}</label>
                <input
                  ref={(el) => { fieldRefs.current.sortOrder = el; }}
                  type="number"
                  min={0}
                  value={formData.sortOrder}
                  onChange={(e) => handleFieldChange('sortOrder', e.target.value)}
                  onBlur={() => handleBlur('sortOrder')}
                  className={`h-10 w-full rounded-lg border px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${formErrors.sortOrder && formTouched.sortOrder ? 'border-red-300' : 'border-slate-200'}`}
                />
                {formErrors.sortOrder && formTouched.sortOrder && <p className="mt-1 text-xs text-red-600">{formErrors.sortOrder}</p>}
              </div>

              <div>
                <label className="mb-1 block text-sm text-slate-600">{t('categoriesStatus')}</label>
                <select
                  value={statusValue}
                  onChange={(e) => {
                    if (e.target.value === 'active') handleFieldChange('isActive', true);
                    else if (e.target.value === 'inactive') handleFieldChange('isActive', false);
                  }}
                  className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <option value="active">{t('categoriesActive')}</option>
                  <option value="inactive">{t('categoriesInactive')}</option>
                </select>
              </div>

              {/* Ảnh danh mục (sheet 2 mục #14) — upload lấy URL rồi gửi kèm
                  image_urls lúc lưu form */}
              {/* Set thẳng qua setFormData: `handleFieldChange` chỉ nhận
                  string|boolean|null (nó còn chạy validate theo field), còn
                  đây là mảng và không có validation nào. */}
              <ImageUploadField
                label={t('categoryImage')}
                value={formData.imageUrls}
                onChange={(urls) => setFormData((prev) => ({ ...prev, imageUrls: urls }))}
                entityType="category"
              />

              {submitError && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>
              )}
            </>
          )}
        </div>

        <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800">
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading}
            className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {isSubmitting ? t('savingBtn') : t('saveBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
